import { AnalyticsSkuViewed } from '@ria-client/segment/Analytics/AnalyticsSkuViewed';
import { SEGMENT_SKU_VIEWED_SOURCE } from '@ria-client/segment/Analytics/constants';
import { MonetizableEnum } from '@ria-client/segment/enums';
import {
  BankingSubProductTypeEnum,
  MarketplaceEntityTypeEnum,
} from '@ria-client/supergraph/marketplace/enums';
import type { DriverType } from '@ria-client/supergraph/marketplace/types';
import { TableCell } from '@ria-client/tw-shadcn/components/table';
import { CtaButton, NumericLayout, RiaDisclaimer } from '@ria-client/tw-shared-ui';
import type { CSSProperties } from 'react';
import { useBestOfAwardsConfig } from '../../BestOfAwards/context';
import {
  BANNER_HEIGHT_FIXED_PX,
  BestOfAwardsRoundupBanner,
} from '../../BestOfAwards/roundup-banner';
import { CdApysDriver } from '../../nodes/financial-advisors-roundup-sections/cd-apys-driver';
import { BankInstitutionCell } from './bank-institution-cell';
import { DriverCell } from './driver-cell';
import type { SummaryTableRowWithDrivers } from './types';

export interface SummaryTableRowProps {
  index: number;
  row: SummaryTableRowWithDrivers;
}

const CELL_CLASS =
  'border-x-0 border-t-0 border-b border-border align-top [&:not(:first-child)]:border-l-0';

export function SummaryTableRow({ index, row }: SummaryTableRowProps) {
  const {
    categoryName,
    oneYearApyEntity,
    threeYearApyEntity,
    fiveYearApyEntity,
    marketplaceEntity: {
      displayName,
      drivers,
      entityType,
      id,
      image,
      institution,
      isFidelityOffer,
      isRiaOffer,
      monetizable: defaultMonetizable,
      name,
      offerForCta,
      periodConsideredDate,
      product,
      productType,
      reviewLink,
      reviewedAtDate,
      riaDisclaimerText,
      starRating,
      subProductType,
      ctaOverride,
      cta,
    },
  } = row;
  const monetizable = offerForCta?.monetizable ?? defaultMonetizable ?? MonetizableEnum.no;
  const offerId = offerForCta?.id || id;
  const offerName = offerForCta?.name || name;
  const ctaToUse = ctaOverride ?? cta;
  const analyticsPayload = {
    index,
    ...(institution?.id ? { institutionId: institution.id } : {}),
    ...(entityType === MarketplaceEntityTypeEnum.Institutions ? { institutionId: id } : {}),
    offerId,
    ...(product?.id ? { productId: product.id } : {}),
    productType,
    monetizable,
  };
  const isCDSubproductType = subProductType === BankingSubProductTypeEnum.CD;
  const { hasBestOfAward, config } = useBestOfAwardsConfig(offerId);
  const cellPaddingTop = hasBestOfAward ? `calc(${BANNER_HEIGHT_FIXED_PX}px + 12px)` : '12px';

  return (
    <AnalyticsSkuViewed
      as="tr"
      className={`${hasBestOfAward ? 'sticky' : ''} scroll-mb-[65px]`}
      id={`summary-table-row-${index}`}
      style={{ '--cell-padding-top': cellPaddingTop } as CSSProperties}
      additionalAttributes={{ page_type: 'roundup' }}
      {...(institution ? { institutionId: institution.id, institutionName: institution.name } : {})}
      {...(entityType === MarketplaceEntityTypeEnum.Institutions
        ? { institutionId: id, institutionName: name }
        : {})}
      {...(product ? { productId: product.id, productName: product.name } : {})}
      impressionPosition={index}
      impressionSection="summary_table_section"
      level="offer"
      monetizable={monetizable}
      name={offerName}
      offerId={offerId}
      offerName={offerName}
      productType={productType}
      source={SEGMENT_SKU_VIEWED_SOURCE}
    >
      <TableCell
        className={`${CELL_CLASS} relative w-[190px] bg-white py-3 pt-[var(--cell-padding-top)] pl-0 pr-2 md:min-w-[190px] ${drivers.length ? 'md:w-[190px]' : 'md:w-auto'}`}
      >
        {hasBestOfAward ? (
          <div className="absolute top-0 left-0 w-full">
            <BestOfAwardsRoundupBanner>{config?.awardText}</BestOfAwardsRoundupBanner>
          </div>
        ) : null}
        <BankInstitutionCell
          analyticsPayload={analyticsPayload}
          cta={ctaToUse ?? null}
          displayName={displayName}
          image={image}
          isFidelityOffer={isFidelityOffer}
          isRiaOffer={isRiaOffer}
          riaDisclaimerText={riaDisclaimerText}
        />
      </TableCell>
      <TableCell
        className={`${CELL_CLASS} w-[230px] min-w-[130px] px-2 py-3 pt-[var(--cell-padding-top)]`}
      >
        <div className="flex flex-col">
          <NumericLayout
            hideLabel
            iconSize={{ xs: 16, md: 17 }}
            reviewLink={reviewLink}
            starRating={starRating}
          />
          {isRiaOffer && periodConsideredDate && reviewedAtDate ? (
            <div className="mt-2 flex flex-col">
              <span className="typography-text-small text-muted-foreground">{reviewedAtDate}</span>
              <span className="typography-text-small text-muted-foreground">
                {periodConsideredDate}
              </span>
            </div>
          ) : categoryName ? (
            <span className="typography-text-small text-muted-foreground">{categoryName}</span>
          ) : null}
        </div>
      </TableCell>
      {drivers.map((driver: DriverType, driverIndex: number) => (
        <TableCell
          className={`${CELL_CLASS} w-[190px] min-w-[130px] overflow-hidden px-2 py-3 pt-[var(--cell-padding-top)]`}
          key={driverIndex}
        >
          {driver.id?.includes('apy') && isCDSubproductType ? (
            <CdApysDriver
              driver={driver}
              fiveYearApyEntity={fiveYearApyEntity}
              oneYearApyEntity={oneYearApyEntity}
              threeYearApyEntity={threeYearApyEntity}
            />
          ) : (
            <DriverCell driver={driver} />
          )}
        </TableCell>
      ))}
      <TableCell
        className={`${CELL_CLASS} hidden w-[159px] px-2 py-3 pt-[var(--cell-padding-top)] md:table-cell`}
      >
        <div className="hidden w-[159px] md:block">
          {ctaToUse ? (
            <CtaButton
              analyticsPayload={analyticsPayload}
              className="h-[29px] md:h-[45px]"
              cta={ctaToUse}
            />
          ) : null}
          {isRiaOffer && riaDisclaimerText ? (
            <div className="mt-2">
              <RiaDisclaimer
                isFidelityOffer={isFidelityOffer}
                monetizable={defaultMonetizable}
                riaDisclaimerText={riaDisclaimerText}
              />
            </div>
          ) : null}
        </div>
      </TableCell>
    </AnalyticsSkuViewed>
  );
}
